import { API_BASE } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { X, Plane, Loader2 } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Airport coordinates (samma som backend airport_coords.py — håller bara
// de vanligaste resmålen från Swedavia + några europeiska huvudstäder.
// Allt utöver detta hämtas via backend GET /api/airports/coords/{iata}).
const AIRPORT_COORDS = {
  ARN: [59.6519, 17.9186], BMA: [59.3544, 17.9416], GOT: [57.6627, 12.2797],
  MMX: [55.5361, 13.3761], LLA: [65.5436, 22.122], UME: [63.7918, 20.2828],
  VBY: [57.6628, 18.3463], OSD: [63.1944, 14.5003], KRN: [67.8217, 20.3367],
  RNB: [56.2667, 15.2653],
  PMI: [39.5519, 2.7388], AGP: [36.6749, -4.4991], ALC: [38.2822, -0.5582],
  BCN: [41.2974, 2.0833], MAD: [40.4719, -3.5626], LHR: [51.4700, -0.4543],
  CDG: [49.0097, 2.5479], AMS: [52.3105, 4.7683], FRA: [50.0379, 8.5622],
  IST: [40.9769, 28.8146], DXB: [25.2532, 55.3657], JFK: [40.6413, -73.7781],
  LAX: [33.9416, -118.4085], NRT: [35.7720, 140.3929], BKK: [13.6900, 100.7501],
  CPH: [55.6181, 12.6561], OSL: [60.1939, 11.1004], HEL: [60.3172, 24.9633],
};

function _normalizeIso(iso) {
  return typeof iso === "string" ? iso.replace(" ", "T") : iso;
}

function _hasCoords(pos) {
  if (!pos) return false;
  const lat = pos.lat ?? pos.latitude;
  const lng = pos.lng ?? pos.longitude ?? pos.lon;
  return lat != null && lng != null;
}

function _coords(pos) {
  // ADS-B källor använder `latitude`/`longitude`, vissa använder `lat`/`lng`
  const lat = Number(pos.lat ?? pos.latitude);
  const lng = Number(pos.lng ?? pos.longitude ?? pos.lon);
  return [lat, lng];
}

async function fetchAirportCoords(iata) {
  if (!iata) return null;
  if (AIRPORT_COORDS[iata]) return AIRPORT_COORDS[iata];
  try {
    const token = localStorage.getItem("grindstugatan_token");
    const r = await fetch(
      `${API_BASE}/api/airports/coords/${iata}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!r.ok) return null;
    const j = await r.json();
    if (j.lat != null && j.lng != null) return [j.lat, j.lng];
  } catch { /* ignore */ }
  return null;
}

function rotatedPlaneIcon(heading) {
  // SVG-flygplan pekar uppåt (norr=0°) som default. Heading 90°=öster, 180°=söder, 268°=västerut.
  const html = `
    <div style="transform: rotate(${heading || 0}deg); width:36px; height:36px; display:flex; align-items:center; justify-content:center; filter: drop-shadow(0 2px 3px rgba(0,0,0,0.45));">
      <svg viewBox="0 0 24 24" width="36" height="36">
        <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
              fill="#FF7A1A" stroke="#fff" stroke-width="1.2"/>
      </svg>
    </div>`;
  return L.divIcon({
    html,
    className: "",
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

export default function FlightMapModal({ open, onClose, watch }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const initialFitDoneRef = useRef(false);
  const planeMarkerRef = useRef(null);
  const trailRef = useRef(null);
  const trailDotsRef = useRef([]);
  const [livePos, setLivePos] = useState(() => {
    const pos = (watch?.last_status || {}).live_position;
    return _hasCoords(pos) ? pos : null;
  });
  const [loading, setLoading] = useState(false);

  // Init Leaflet karta när modalen öppnas
  useEffect(() => {
    if (!open || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true, attributionControl: true,
    }).setView([55, 10], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    mapRef.current = map;
    return () => {
      try { map.remove(); } catch { /* ignore */ }
      mapRef.current = null;
      planeMarkerRef.current = null;
      trailRef.current = null;
      trailDotsRef.current = [];
      initialFitDoneRef.current = false;
    };
  }, [open]);

  // Hämta flygplats-koordinater och rita rutt
  useEffect(() => {
    if (!open || !mapRef.current || !watch) return;
    const s = watch.last_status || {};
    const isArrival = watch.kind === "arrival";
    const startIata = isArrival ? (s.origin || watch.origin) : watch.airport_iata;
    const endIata = isArrival ? watch.airport_iata : (s.destination || watch.destination);
    (async () => {
      const startCoord = await fetchAirportCoords(startIata);
      const endCoord = await fetchAirportCoords(endIata);
      if (!mapRef.current) return;
      if (startCoord) {
        L.marker(startCoord, {
          icon: L.divIcon({
            html: '<div style="font-size:22px">🛫</div>',
            className: "", iconSize: [24, 24], iconAnchor: [12, 12],
          }),
        }).addTo(mapRef.current).bindTooltip(startIata || "Avgång", { permanent: false });
      }
      if (endCoord) {
        L.marker(endCoord, {
          icon: L.divIcon({
            html: '<div style="font-size:22px">🛬</div>',
            className: "", iconSize: [24, 24], iconAnchor: [12, 12],
          }),
        }).addTo(mapRef.current).bindTooltip(endIata || "Ankomst", { permanent: false });
      }
      if (startCoord && endCoord && !initialFitDoneRef.current) {
        trailRef.current = L.polyline([startCoord, endCoord], {
          color: "#FF7A1A", weight: 2, opacity: 0.6, dashArray: "6, 8",
        }).addTo(mapRef.current);
        mapRef.current.fitBounds([startCoord, endCoord], { padding: [50, 50] });
        initialFitDoneRef.current = true;
      } else if (startCoord && !initialFitDoneRef.current) {
        mapRef.current.setView(startCoord, 6);
        initialFitDoneRef.current = true;
      }
    })();
  }, [open, watch]);

  // Polla live-position var 15:e sekund
  useEffect(() => {
    if (!open || !watch) return;
    let cancelled = false;
    const fetchPos = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("grindstugatan_token");
        const r = await fetch(
          `${API_BASE}/api/flights/watched/${watch.id}/poll`,
          { method: "POST", headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok || cancelled) return;
        const j = await r.json();
        const pos = (j.watch?.last_status || {}).live_position
          || (watch.last_status || {}).live_position;
        if (_hasCoords(pos)) setLivePos(pos);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchPos();
    const id = setInterval(fetchPos, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, watch]);

  // Initial ritning av lagrad historik från DB (synlig oberoende av session)
  useEffect(() => {
    if (!open || !mapRef.current || !watch) return;
    const hist = (watch.last_status || {}).position_history || [];
    hist.forEach((pt) => {
      if (pt.lat == null || pt.lng == null) return;
      const dot = L.circleMarker([pt.lat, pt.lng], {
        radius: 3, color: "#FF7A1A", fillColor: "#FF7A1A",
        fillOpacity: 0.7, weight: 1, opacity: 0.7,
      }).addTo(mapRef.current);
      trailDotsRef.current.push(dot);
    });
  }, [open, watch]);

  // Rita / uppdatera flygplans-marker + lägg dit en breadcrumb-dot
  // för varje ny position (max 200 punkter sparade).
  useEffect(() => {
    if (!mapRef.current || !livePos || !_hasCoords(livePos)) return;
    const latlng = _coords(livePos);
    const heading = livePos.heading || livePos.true_track || 0;
    if (!planeMarkerRef.current) {
      planeMarkerRef.current = L.marker(latlng, {
        icon: rotatedPlaneIcon(heading), zIndexOffset: 1000,
      }).addTo(mapRef.current);
      planeMarkerRef.current.bindTooltip(
        `${watch?.flight_id || "Flyg"} · ${Math.round(livePos.altitude_m || 0)}m`,
        { permanent: false },
      );
      // Första gången: centrera planet (utan att ändra zoom om den redan satts av fitBounds)
      mapRef.current.panTo(latlng);
      // Lägg första breadcrumb-dot
      const dot = L.circleMarker(latlng, {
        radius: 3, color: "#FF7A1A", fillColor: "#FF7A1A",
        fillOpacity: 0.7, weight: 1, opacity: 0.7,
      }).addTo(mapRef.current);
      trailDotsRef.current.push(dot);
    } else {
      // Ny position — flytta planet och lägg gamla positionen som breadcrumb
      const prevLatLng = planeMarkerRef.current.getLatLng();
      const isNewPos = (
        Math.abs(prevLatLng.lat - latlng[0]) > 0.001 ||
        Math.abs(prevLatLng.lng - latlng[1]) > 0.001
      );
      if (isNewPos) {
        const dot = L.circleMarker([prevLatLng.lat, prevLatLng.lng], {
          radius: 3, color: "#FF7A1A", fillColor: "#FF7A1A",
          fillOpacity: 0.7, weight: 1, opacity: 0.7,
        }).addTo(mapRef.current);
        trailDotsRef.current.push(dot);
        // Begränsa till 200 dots — släng den äldsta om vi överstiger
        if (trailDotsRef.current.length > 200) {
          const oldest = trailDotsRef.current.shift();
          try { mapRef.current.removeLayer(oldest); } catch { /* ignore */ }
        }
      }
      planeMarkerRef.current.setLatLng(latlng);
      planeMarkerRef.current.setIcon(rotatedPlaneIcon(heading));
      planeMarkerRef.current.setTooltipContent(
        `${watch?.flight_id || "Flyg"} · ${Math.round(livePos.altitude_m || 0)}m`,
      );
    }
  }, [livePos, watch]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-2 sm:p-4"
      data-testid="flight-map-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[color:var(--bg-elevated)] rounded-xl w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--border-subtle)]">
          <div className="flex items-center gap-2 text-sm">
            <Plane size={16} className="text-[color:var(--jarvis)]" />
            <span className="font-semibold">
              {watch?.flight_id || "Flyg"} · Live karta
            </span>
            {loading && <Loader2 size={12} className="animate-spin text-[color:var(--text-tertiary)]" />}
            {livePos && (
              <span className="text-[10px] text-[color:var(--text-tertiary)] tabular-nums">
                {Math.round(livePos.altitude_m || 0)}m · {Math.round((livePos.speed_ms || 0) * 3.6)} km/h
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[color:var(--bg-subtle)] rounded"
            data-testid="flight-map-close"
            aria-label="Stäng karta"
          >
            <X size={16} />
          </button>
        </div>
        <div
          ref={containerRef}
          className="flex-1 w-full"
          data-testid="flight-map-container"
        />
        {!livePos && (
          <div className="px-3 py-2 text-[11px] text-[color:var(--text-tertiary)] text-center border-t border-[color:var(--border-subtle)]">
            Väntar på ADS-B-data… (planet visas så fort det är i luften)
          </div>
        )}
      </div>
    </div>
  );
}
